import { App } from "antd";
import { useMutation } from "@tanstack/react-query";
import api from "../../utils/api";

const DEFAULT_ADMISSION_LETTER_FILENAME = "admission-letter";

const getFileNameFromContentDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return DEFAULT_ADMISSION_LETTER_FILENAME;
  }

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  const fileName = utf8FileNameMatch?.[1] || fileNameMatch?.[1];

  return fileName ? decodeURIComponent(fileName.trim()) : DEFAULT_ADMISSION_LETTER_FILENAME;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getErrorMessageFromBlob = async (error: any) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      return JSON.parse(text)?.message;
    } catch {
      return undefined;
    }
  }

  return data?.message;
};

const getAdmissionLetter = async () => {
  const response = await api.get("/ReadMore/admission-letter/download", {
    responseType: "blob",
  });

  return {
    blob: response.data as Blob,
    fileName: getFileNameFromContentDisposition(
      response.headers["content-disposition"],
    ),
  };
};

export const useDownloadAdmissionLetterQuery = () => {
  const { notification } = App.useApp();

  const mutation = useMutation({
    mutationFn: getAdmissionLetter,
  });

  const downloadAdmissionLetter = async () => {
    try {
      const { blob, fileName } = await mutation.mutateAsync();
      downloadBlob(blob, fileName);

      notification.success({
        message: "Success",
        description: "Admission letter downloaded successfully",
      });
    } catch (error: any) {
      const message = await getErrorMessageFromBlob(error);

      notification.error({
        message: "Error",
        description: message || "Failed to download admission letter",
      });
    }
  };

  return {
    ...mutation,
    downloadAdmissionLetter,
    isLoading: mutation.isPending,
  };
};

export default useDownloadAdmissionLetterQuery;
